import { Address, Client, Keypair, Pubky, PublicKey, resolvePubky, Session, Signer } from '@synonymdev/pubky';
import type { MigrationLock, MigrationReport, ProgressEvent } from 'pubky-social-migrator';
import type { TKeypairParams } from '@/application/auth/auth.types';
import { getAuthClientId } from '@/config/auth';
import {
  getHomeserver,
  getHomeserverUrl,
  getPkarrRelays,
  getTestnet,
  isStagingHomeserverDeploy,
} from '@/config/network';
import type { ActiveSessionFailure, SessionReference } from '@/libs/auth/session.types';
import { AppError } from '@/libs/error/error';
import { AuthErrorCode, DatabaseErrorCode, ServerErrorCode, ValidationErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { httpResponseToError } from '@/libs/error/error.http';
import { ErrorCategory, ErrorService } from '@/libs/error/error.types';
import { hasHttpStatus, toAppError } from '@/libs/error/error.utils';
import { HttpMethod, HttpStatusCode } from '@/libs/http/http.types';
import { Identity } from '@/libs/identity/identity';
import { Logger } from '@/libs/logger/logger';
import { HOMESERVER_EVENT_STREAM_SUBSCRIBE_OPERATION } from '@/libs/observability/sentry.constants';
import { sleep } from '@/libs/utils/utils';
import type { Pubky as TPubkyModel } from '@/models/models.types';
import type {
  TGenerateAuthUrlResult,
  THomeserverSessionResult,
  THomeserverSignUpParams,
  TSignupTokenVerificationStatus,
} from '@/services/homeserver/homeserver.types';
import { useAuthStore } from '@/stores/auth/auth.store';
import { extractStatusCode, handleError, isPubkyErrorLike } from './error.utils';
import { type GrantFlowRequest, GrantFlowService } from './grant-flow';
import type {
  StoragePath,
  THomeserverBytesResult,
  THomeserverFetchParams,
  THomeserverListAllParams,
  THomeserverListParams,
  THomeserverPublicKeyParams,
  THomeserverRequestParams,
  THomeserverUserEvent,
  TOwnedSessionPath,
  TPutBlobParams,
} from './homeserver.types';
import {
  assertOk,
  extractPubkyZ32,
  getOwnedResponse,
  isHttpUrl,
  parseResponseOrUndefined,
  PUBKY_PREFIX,
  resolveOwnedSessionPath,
} from './homeserver.utils';
import { retryHomeserverWrite, type THomeserverWriteFence } from './write-retry';

const PRIVATE_STORAGE_PREFIX = '/priv/';
const STORAGE_PATH_PREFIXES = ['/pub/', PRIVATE_STORAGE_PREFIX] as const;
const DELETE_IDEMPOTENT_MAX_ATTEMPTS = 3;
const DELETE_IDEMPOTENT_RETRY_DELAY_MS = 500;
/** Default limit for list operations */
const LIST_DEFAULT_LIMIT = 500;

type HomeserverSdkUserEvent = THomeserverUserEvent & {
  free(): void;
};

export class HomeserverService {
  private constructor() {}

  private static pubkySdk: Pubky | null = null;

  private static sessionFailureListeners = new Set<(failure: ActiveSessionFailure) => void>();

  static subscribeSessionFailures(listener: (failure: ActiveSessionFailure) => void): () => void {
    this.sessionFailureListeners.add(listener);
    return () => {
      this.sessionFailureListeners.delete(listener);
    };
  }

  /** Observe only owned requests, after the SDK has exhausted its bearer refresh/retry. */
  private static async withActiveSession<T>(session: Session, request: () => Promise<T>): Promise<T> {
    const snapshot = useAuthStore.getState();
    const generation = snapshot.generation;
    try {
      return await request();
    } catch (error) {
      const terminal =
        extractStatusCode(error) === HttpStatusCode.UNAUTHORIZED ||
        (error instanceof AppError &&
          error.service === ErrorService.Homeserver &&
          error.code === AuthErrorCode.SESSION_EXPIRED) ||
        (isPubkyErrorLike(error) &&
          error.name === 'AuthenticationError' &&
          extractStatusCode(error) !== HttpStatusCode.FORBIDDEN) ||
        (snapshot.sessionReference && this.unusableGrantReason(error, snapshot.sessionReference.sessionStoreId));
      if (terminal && snapshot.session === session && snapshot.restoreStatus === 'ready') {
        for (const listener of this.sessionFailureListeners) {
          try {
            listener({ session, generation });
          } catch {
            // A lifecycle observer must not replace the original request error or prevent rollback.
          }
        }
      }
      throw error;
    }
  }

  /** Throws once logout begins, the account changes, or the session is replaced after this call. */
  private static sessionStillCurrent(context: Record<string, unknown>): () => void {
    const start = useAuthStore.getState();
    const session = start.selectSession();
    const generation = start.generation;
    return () => {
      const state = useAuthStore.getState();
      if (!state.isLoggingOut && state.selectSession() === session && state.generation === generation) return;
      throw Err.auth(AuthErrorCode.SESSION_EXPIRED, 'Session ended before the homeserver write completed.', {
        service: ErrorService.Homeserver,
        operation: 'writeRetry',
        context,
      });
    };
  }

  /**
   * Fences write retries to the session that started them: a retry never runs after logout
   * begins, the account changes, or the session is replaced. The first attempt is unguarded,
   * as before retries existed, unless it belongs to a later pass of a read-check-write (`fence`).
   */
  private static writeRetryGuard(
    url: string,
    method: HttpMethod,
    fence: THomeserverWriteFence | undefined,
  ): (attempt: number) => void {
    const assertCurrent = this.sessionStillCurrent({ url, method });
    return (attempt) => {
      if (attempt === 0) fence?.assertCurrent();
      else assertCurrent();
    };
  }

  /**
   * The fence for one read-check-write (`retryHomeserverWriteAfterReread`), taken when it starts.
   * Its first pass runs as any write does; a later pass, and each write in it, throws
   * `SESSION_EXPIRED` once logout began or the session or account changed. `guardFirstPass`
   * checks the first pass too, for work that waits in a queue before its first pass or reads
   * before its first write.
   */
  static createWriteFence({ guardFirstPass = false }: { guardFirstPass?: boolean } = {}): THomeserverWriteFence {
    const assertCurrent = this.sessionStillCurrent({ operation: 'writeAfterReread' });
    let pass = 0;
    const guarded = () => guardFirstPass || pass > 0;
    return {
      enter: (next) => {
        pass = next;
        if (guarded()) assertCurrent();
      },
      assertCurrent: () => {
        if (guarded()) assertCurrent();
      },
    };
  }

  /** The pending owned write per session and path, so writes to one file never overlap. */
  private static pathWrites = new WeakMap<Session, Map<string, Promise<unknown>>>();

  /**
   * Runs owned writes to the same file one after another, retries included, so an older
   * write's retry cannot land after a newer one (a tag removed while its add waits out a 429).
   * A write with nothing ahead of it starts in the caller's tick. One that waited checks the
   * session it was queued under before it starts, and throws `SESSION_EXPIRED` if it ended.
   */
  private static async inPathOrder<T>(session: Session, path: string, url: string, write: () => Promise<T>) {
    let pending = this.pathWrites.get(session);
    if (!pending) {
      pending = new Map();
      this.pathWrites.set(session, pending);
    }
    const previous = pending.get(path);
    let run: Promise<T>;
    if (previous) {
      const assertCurrent = this.sessionStillCurrent({ url, operation: 'queuedWrite' });
      run = previous
        .catch(() => {})
        .then(() => {
          assertCurrent();
          return write();
        });
    } else {
      run = write();
    }
    pending.set(path, run);
    try {
      return await run;
    } finally {
      if (pending.get(path) === run) pending.delete(path);
    }
  }

  /**
   * Gets the Pubky SDK singleton.
   */
  private static getPubkySdk(): Pubky {
    if (!this.pubkySdk) {
      if (getTestnet()) {
        this.pubkySdk = Pubky.testnet();
      } else {
        const client = new Client({ pkarr: { relays: getPkarrRelays() } });
        this.pubkySdk = Pubky.withClient(client);
      }
    }
    return this.pubkySdk;
  }

  private static resolveOwnedSessionPath(url: string): TOwnedSessionPath | null {
    const session = useAuthStore.getState().selectSession();
    return resolveOwnedSessionPath({ url, session, allowedPrefixes: STORAGE_PATH_PREFIXES });
  }

  /**
   * Gets a signer for the homeserver
   * @param keypair - The keypair to get a signer for
   * @returns The signer
   */
  private static getSigner(keypair: Keypair): Signer {
    const pubkySdk = this.getPubkySdk();
    return pubkySdk.signer(keypair);
  }

  /**
   * Resolves the key's homeserver from its PKARR record.
   * @returns The homeserver public key, or `null` when the record is provably absent
   * @throws When the lookup itself failed (relay/network error) — absence NOT proven.
   *   The SDK rejects with `PkarrError`, which is mapped to a retryable Network error.
   */
  private static async resolveHomeserverRecord({ publicKey }: THomeserverPublicKeyParams) {
    try {
      const pubkySdk = this.getPubkySdk();
      const homeserver = await pubkySdk.getHomeserverOf(publicKey);
      return homeserver ?? null;
    } catch (error) {
      return handleError({
        error,
        additionalContext: { publicKey: publicKey?.z32?.(), operation: 'resolveHomeserverRecord' },
      });
    }
  }

  /**
   * Resolve PKARR homeserver; on staging, require it matches this deploy's homeserver.
   *
   * An absent record is rejected the same way as a mismatched one: absence cannot
   * prove the key belongs here, both outcomes are deterministic (retrying the
   * lookup cannot change them), and fail-open would re-enable the prod-key
   * force-republish this guard exists to prevent (#2126). This also means a key
   * whose just-published record has not yet propagated to our relays is rejected
   * until it propagates. Only a lookup that itself failed (relay/network error)
   * throws a retryable error instead (Network for the SDK's `PkarrError`,
   * Server for anything else).
   */
  static async assertUserHomeserverAllowed({ publicKey }: THomeserverPublicKeyParams): Promise<void> {
    if (!isStagingHomeserverDeploy()) {
      return;
    }

    const homeserver = await this.resolveHomeserverRecord({ publicKey });
    const configuredHomeserver = getHomeserver();
    const resolvedHomeserver = homeserver ? homeserver.z32() : null;
    if (resolvedHomeserver !== configuredHomeserver) {
      const context = {
        configuredHomeserver,
        resolvedHomeserver,
        publicKey: publicKey.z32(),
      };
      // An expected user mistake (prod key on a staging deploy), not a system
      // fault — constructed directly rather than via the Err factory so every
      // occurrence does not emit an error log plus a Sentry error event.
      Logger.info('Rejected sign-in: key is not linked to this staging homeserver', context);
      throw new AppError({
        category: ErrorCategory.Auth,
        code: AuthErrorCode.WRONG_ENVIRONMENT_HOMESERVER,
        message: 'This key is not linked to this staging deploy homeserver.',
        service: ErrorService.Homeserver,
        operation: 'assertUserHomeserverAllowed',
        context,
      });
    }
  }

  /**
   * Verifies a signup token (invite code) against the homeserver.
   *
   * Performs a GET to the homeserver's `/signup_tokens/<token>` endpoint. This is a
   * homeserver-root endpoint that cannot be reached via the homeserver pubkey (its PKARR
   * record has no resolvable HTTPS endpoint), so the explicit {@link getHomeserverUrl} is used.
   *
   * A definitive homeserver response distinguishes valid, used, and unknown tokens, whereas
   * a failure to reach the homeserver is surfaced as a thrown error so callers can tell
   * verification outcomes apart from "couldn't verify right now".
   *
   * @param signupToken - The signup token / invite code to verify
   * @returns `'valid'` when the token exists and is unused, `'used'` when already redeemed,
   *   `'invalid'` when the homeserver does not recognise the token (404).
   * @throws When the homeserver could not be reached (network error, timeout, DNS/PKARR failure)
   *   or returns an unexpected status.
   */
  static async verifySignupToken(signupToken: string): Promise<TSignupTokenVerificationStatus> {
    const url = `${getHomeserverUrl()}/signup_tokens/${encodeURIComponent(signupToken)}`;
    try {
      const response = await this.getPubkySdk().client.fetch(url, { method: HttpMethod.GET, credentials: 'omit' });
      Logger.debug('Signup token verification response', { status: response.status });

      if (response.status === HttpStatusCode.NOT_FOUND) {
        return 'invalid';
      }

      if (!response.ok) {
        throw new Error(`Signup token verification failed with status ${response.status}`);
      }

      const data = (await response.json()) as { status?: string };
      if (data.status === 'valid') {
        return 'valid';
      }
      if (data.status === 'used') {
        return 'used';
      }

      Logger.warn('Unexpected signup token verification payload', { status: data.status });
      return 'invalid';
    } catch (error) {
      Logger.warn('Signup token verification could not reach the homeserver', { error });
      throw error;
    }
  }

  /**
   * Signs in a user to the homeserver.
   *
   * If signin fails due to missing homeserver records, this method will attempt to republish
   * the homeserver and return `undefined` to signal the caller should retry the signin.
   *
   * @param keypair - The keypair to sign in with
   * @returns The session result, or `undefined` if homeserver was republished and caller should retry
   */
  static async signIn({ keypair }: TKeypairParams): Promise<THomeserverSessionResult | undefined> {
    await this.assertSessionStoreAvailable();
    const signer = this.getSigner(keypair);

    if (isStagingHomeserverDeploy()) {
      // Fail closed on staging: an inconclusive PKARR lookup must never migrate an
      // unverified key to the configured staging homeserver.
      await this.assertUserHomeserverAllowed({ publicKey: keypair.publicKey });
    } else {
      // Lookup-failure hardening: self-heal (republish) only a PROVABLY ABSENT
      // record (e.g. expired from the DHT). A failed lookup throws instead —
      // republishing on a transient error could overwrite an existing record
      // that points at another homeserver. A failed grant exchange never republishes it.
      const homeserverRecord = await this.resolveHomeserverRecord({ publicKey: keypair.publicKey });
      if (homeserverRecord === null) {
        return await this.republishConfiguredHomeserver({
          signer,
          keypair,
          originalError: 'homeserver record absent from PKARR',
        });
      }
    }

    try {
      const session = await signer.signin(getAuthClientId());
      return { session };
    } catch (signinError) {
      return handleError({ error: signinError, additionalContext: { operation: 'signIn' } });
    }
  }

  /** Account creation and session issuance are separate so retries cannot spend the invite twice. */
  static async createAccount({ keypair, signupToken }: THomeserverSignUpParams): Promise<void> {
    try {
      await this.assertSessionStoreAvailable();
      await this.getSigner(keypair).signup(PublicKey.from(getHomeserver()), signupToken);
    } catch (error) {
      return handleError({ error, additionalContext: { operation: 'createAccount' } });
    }
  }

  static async signInCreatedAccount({ keypair }: TKeypairParams): Promise<THomeserverSessionResult> {
    try {
      await this.assertSessionStoreAvailable();
      const signer = this.getSigner(keypair);
      const record = await this.resolveHomeserverRecord({ publicKey: keypair.publicKey });
      // Only this signup-specific path knows the generated key's intended homeserver.
      // A lost create response can leave an account without its PKARR publication.
      if (record === null) await signer.pkdns.publishHomeserverForce(PublicKey.from(getHomeserver()));
      else if (record.z32() !== getHomeserver()) {
        throw Err.auth(AuthErrorCode.WRONG_ENVIRONMENT_HOMESERVER, 'This signup belongs to another homeserver.', {
          service: ErrorService.Homeserver,
          operation: 'signInCreatedAccount',
        });
      }
      return { session: await signer.signin(getAuthClientId()) };
    } catch (error) {
      return handleError({ error, additionalContext: { operation: 'signInCreatedAccount' } });
    }
  }

  static async saveSession(session: Session): Promise<SessionReference> {
    try {
      if (!session.grant) {
        throw Err.auth(AuthErrorCode.UNAUTHORIZED, 'A grant session is required.', {
          service: ErrorService.Homeserver,
          operation: 'saveSession',
        });
      }
      const info = await session.grant.sessionInfo();
      if (info.clientId !== getAuthClientId()) {
        throw Err.auth(AuthErrorCode.UNAUTHORIZED, 'The approved grant belongs to another application.', {
          service: ErrorService.Homeserver,
          operation: 'saveSession',
        });
      }
      await this.assertSessionStoreAvailable();
      const saved = await this.getPubkySdk()
        .browserSessionStore.save(session)
        .catch((error) => {
          if (isPubkyErrorLike(error) && error.name === 'ClientStateError') {
            throw Err.database(DatabaseErrorCode.WRITE_FAILED, 'Could not save the authenticated session.', {
              service: ErrorService.Local,
              operation: 'saveSession',
              cause: error,
            });
          }
          throw error;
        });
      return {
        kind: 'grant',
        sessionStoreId: saved.id,
        clientId: info.clientId,
        grantId: info.grantId,
        grantExpiresAt: info.grantExpiresAt,
        tokenExpiresAt: info.tokenExpiresAt,
      };
    } catch (error) {
      return handleError({ error, additionalContext: { operation: 'saveSession' } });
    }
  }

  /** Keep completed approvals recoverable without selecting them or deleting a shared delegated key. */
  static async retainUnusedSession(session: Session, persistence?: Promise<SessionReference>): Promise<void> {
    try {
      await (persistence ?? this.saveSession(session));
      Logger.warn(
        'Grant approval was canceled or superseded; the completed session was retained in SDK storage. Remote revocation was not confirmed.',
      );
    } catch {
      Logger.warn(
        'Grant approval was canceled or superseded; saving the completed session failed. Remote revocation was not confirmed.',
      );
    }
  }

  static async restoreReference(reference: SessionReference): Promise<Session> {
    const store = this.getPubkySdk().browserSessionStore;
    try {
      await this.assertSessionStoreAvailable();
      return await store.restore(reference.sessionStoreId);
    } catch (error) {
      const reason = this.unusableGrantReason(error, reference.sessionStoreId);
      if (reason) {
        throw Err.auth(AuthErrorCode.SESSION_EXPIRED, 'Authorize this account again to restore its session.', {
          service: ErrorService.Homeserver,
          operation: 'restoreGrant',
          context: {
            reason,
            // Diagnostics for #2942: which half of the saved grant was gone.
            ...(reason === 'missing_local_grant' && {
              material: this.isMissingSessionRecord(error, reference.sessionStoreId) ? 'record' : 'key',
            }),
          },
        });
      }
      if (isPubkyErrorLike(error) && error.name === 'ClientStateError') {
        throw Err.database(DatabaseErrorCode.QUERY_FAILED, 'Could not read the saved session. Try again.', {
          service: ErrorService.Local,
          operation: 'restoreGrant',
          cause: error,
        });
      }
      return handleError({ error, additionalContext: { operation: 'restoreGrant' } });
    }
  }

  static async removeSessionRecord(reference: SessionReference): Promise<void> {
    const store = this.getPubkySdk().browserSessionStore;
    try {
      await store.remove(reference.sessionStoreId);
    } catch (error) {
      if (this.isMissingSessionRecord(error, reference.sessionStoreId)) return;
      return handleError({ error, additionalContext: { operation: 'removeSessionRecord' } });
    }
  }

  private static async assertSessionStoreAvailable(): Promise<void> {
    if (!(await this.getPubkySdk().browserSessionStore.isAvailable())) {
      throw Err.database(
        DatabaseErrorCode.INIT_FAILED,
        'Session storage is unavailable. Try an updated browser or retry.',
        {
          service: ErrorService.Homeserver,
          operation: 'checkSessionStore',
        },
      );
    }
  }

  private static unusableGrantReason(error: unknown, id: string): string | null {
    if (this.isMissingSessionRecord(error, id)) return 'missing_local_grant';
    if (
      typeof error !== 'object' ||
      !error ||
      !('name' in error) ||
      error.name !== 'ClientStateError' ||
      !('message' in error)
    )
      return null;
    // Exact terminal outcomes from SDK 0.16 restore; other ClientStateErrors include retryable storage failures.
    if (error.message === 'Browser session was signed out.') return 'remote_logout_completed';
    if (error.message === 'Browser session is no longer valid.') return 'invalid_grant';
    if (error.message === 'Unsupported stored session version.') return 'unsupported_stored_session';
    // A successful key-store read found no delegated key. Retrying cannot recreate that private key.
    if (typeof error.message === 'string' && /^Delegated grant key not found: .+$/.test(error.message))
      return 'missing_local_grant';
    return null;
  }

  private static isMissingSessionRecord(error: unknown, id: string): boolean {
    // Pinned SDK 0.16 emits this only after a successful IndexedDB read finds no record.
    // list() is unsuitable for checking absence: it also returns [] when IndexedDB fails.
    return (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      error.name === 'ClientStateError' &&
      'message' in error &&
      error.message === `Stored Pubky session not found: ${id}`
    );
  }

  private static async republishConfiguredHomeserver({
    signer,
    keypair,
    originalError,
  }: {
    signer: Signer;
    keypair: Keypair;
    originalError: unknown;
  }): Promise<undefined> {
    try {
      const homeserverPublicKey = PublicKey.from(getHomeserver());
      await signer.pkdns.publishHomeserverForce(homeserverPublicKey);
      Logger.debug('Republish homeserver successful', { keypair: Identity.pubkyFromKeypair(keypair) });
      return undefined;
    } catch (republishError) {
      return handleError({
        error: republishError,
        additionalContext: {
          pubky: Identity.pubkyFromKeypair(keypair),
          originalSigninError: String(originalError),
          operation: 'republishConfiguredHomeserver',
        },
        statusCode: HttpStatusCode.UNAUTHORIZED,
      });
    }
  }

  static async startGrantFlow(request: GrantFlowRequest): Promise<TGenerateAuthUrlResult> {
    try {
      await this.assertSessionStoreAvailable();
      return await GrantFlowService.start(this.getPubkySdk(), request, (session) => this.retainUnusedSession(session));
    } catch (error) {
      return handleError({ error, additionalContext: { operation: 'startGrantFlow' } });
    }
  }

  static clearPendingAuthFlow(): void {
    GrantFlowService.clear();
  }

  /** Revoke a pre-migration cookie; never return it as an app session. */
  static async revokeLegacyCookieSession(exported: string): Promise<void> {
    let session: Session;
    try {
      session = await this.getPubkySdk().restoreSession(exported);
    } catch (error) {
      // SDK 0.16 maps a missing legacy GET /session (401/404) to this exact terminal error.
      if (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        'message' in error &&
        error.name === 'AuthenticationError' &&
        error.message === 'Authentication error: The provided auth request has expired or was cancelled.'
      )
        return;
      return handleError({ error, additionalContext: { operation: 'restoreLegacyCookieForRevocation' } });
    }
    if (session.grant) return;
    try {
      await session.signout();
    } catch (error) {
      if (extractStatusCode(error) === HttpStatusCode.UNAUTHORIZED) return;
      return handleError({ error, additionalContext: { operation: 'revokeLegacyCookieSession' } });
    }
  }

  static async logout({ session }: THomeserverSessionResult) {
    try {
      // SDK 0.16 coordinates logout and can use a grant proof after bearer expiry.
      // Do not send a storage request first: a pending logout intentionally rejects it.
      await session.signout();
    } catch (error) {
      return handleError({ error, additionalContext: { url: 'signout' } });
    }
  }

  /** Revoke an ambient cookie on explicit logout, without restoring a legacy app session. */
  static async logoutLegacyCookie(pubky: string): Promise<void> {
    try {
      const url = `https://_pubky.${PublicKey.from(pubky).z32()}/session`;
      // This idempotent cookie endpoint needs no bearer or persisted session export.
      const response = await this.getPubkySdk().client.fetch(url, {
        method: HttpMethod.DELETE,
        credentials: 'include',
      });
      if (!response.ok) throw httpResponseToError(response, ErrorService.Homeserver, 'logoutLegacyCookie', url);
    } catch (error) {
      return handleError({ error, additionalContext: { operation: 'logoutLegacyCookie' } });
    }
  }

  private static async fetch({ url, options }: THomeserverFetchParams): Promise<Response> {
    try {
      const pubkySdk = this.getPubkySdk();
      const httpBridge = pubkySdk.client;
      // Resolve pubky identifiers to transport URLs before fetching
      const resolvedUrl = url.startsWith(PUBKY_PREFIX) ? resolvePubky(url) : url;
      const response = await httpBridge.fetch(resolvedUrl, {
        method: options?.method,
        body: options?.body as BodyInit | undefined,
        cache: options?.cache,
        credentials: 'omit',
      });

      Logger.debug('Response from homeserver', { response });

      return response;
    } catch (error) {
      return handleError({ error, additionalContext: { url, method: options?.method } });
    }
  }

  /**
   * Performs a request against the homeserver.
   *
   * Sends a JSON payload when provided and throws if the response is not OK.
   * Owned paths use authenticated SDK storage; raw public HTTP requests omit cookies.
   *
   * @param {HttpMethod} method - HTTP method to use (e.g. PUT, POST, DELETE).
   * @param {string} url - Pubky URL.
   * @param {Record<string, unknown>} [bodyJson] - JSON body to serialize and send.
   */
  static async request<T>({ method, url, bodyJson, singleAttempt, fence }: THomeserverRequestParams): Promise<T> {
    const owned = this.resolveOwnedSessionPath(url);
    // A fenced write belongs to a read-check-write, whose loop retries it from the read.
    const retryOptions = singleAttempt || fence ? { maxRetries: 0 } : {};

    // Handle owned session paths
    if (owned) {
      const { session, path } = owned;

      switch (method) {
        case HttpMethod.GET: {
          const response = await this.withActiveSession(session, () => getOwnedResponse({ session, path, url }));
          return (await parseResponseOrUndefined<T>({ response })) as T;
        }
        case HttpMethod.PUT: {
          // Snapshot once so every retry replays the same body even if the caller mutates its
          // object meanwhile. structuredClone, not a JSON round-trip: the SDK converts the object
          // itself (serde_wasm_bindgen), which sends an `undefined` property as `null`.
          const ownedBody = structuredClone(bodyJson ?? {});
          const beforeAttempt = this.writeRetryGuard(url, method, fence);
          await this.inPathOrder(session, path, url, () =>
            retryHomeserverWrite(
              HttpMethod.PUT,
              () => this.withActiveSession(session, () => session.storage.putJson(path, ownedBody)),
              { beforeAttempt, ...retryOptions },
            ),
          ).catch((error) => handleError({ error, additionalContext: { url, method } }));
          return undefined as T;
        }
        case HttpMethod.DELETE: {
          const beforeAttempt = this.writeRetryGuard(url, method, fence);
          await this.inPathOrder(session, path, url, () =>
            retryHomeserverWrite(
              HttpMethod.DELETE,
              () => this.withActiveSession(session, () => session.storage.delete(path)),
              { beforeAttempt, ...retryOptions },
            ),
          ).catch((error) => handleError({ error, additionalContext: { url, method } }));
          return undefined as T;
        }
      }
    }

    // Non-owned: only GET allowed on non-HTTP URLs
    if (method !== HttpMethod.GET && (!isHttpUrl(url) || extractPubkyZ32(url) !== null)) {
      throw Err.validation(
        ValidationErrorCode.INVALID_INPUT,
        'Authenticated writes must target an owned /pub/* or /priv/* path for the current session.',
        {
          service: ErrorService.Homeserver,
          operation: 'request',
          context: { url, method, statusCode: HttpStatusCode.BAD_REQUEST },
        },
      );
    }

    // Handle public requests
    const pubkySdk = this.getPubkySdk();
    const serializedBody = bodyJson === undefined ? undefined : JSON.stringify(bodyJson);
    const fetchPromise =
      method === HttpMethod.GET
        ? isHttpUrl(url)
          ? pubkySdk.client.fetch(url)
          : pubkySdk.publicStorage.get(url as Address)
        : retryHomeserverWrite(
            method,
            () => this.fetch({ url, options: { method, body: serializedBody } }),
            retryOptions,
          );

    const response = await fetchPromise.catch((error) => handleError({ error, additionalContext: { url, method } }));

    await assertOk({ response, url, operation: 'request' });

    return method === HttpMethod.GET ? ((await parseResponseOrUndefined<T>({ response })) as T) : (undefined as T);
  }

  /**
   * Uploads binary data to the homeserver using PUT.
   *
   * Intended for blob contents (e.g., avatars). Throws if the response is not OK.
   * Owned uploads use the active grant through SDK storage.
   *
   * @param {string} url - Pubky URL.
   * @param {Uint8Array} blob - Raw bytes of the blob to upload.
   */
  static async putBlob({ url, blob, fence }: TPutBlobParams) {
    const retryOptions = fence ? { maxRetries: 0 } : {};
    // Uint8Array is mutable; retries must replay the exact original payload.
    const retryBody = blob.slice();
    const owned = this.resolveOwnedSessionPath(url);
    if (owned) {
      const { session, path } = owned;
      try {
        const beforeAttempt = this.writeRetryGuard(url, HttpMethod.PUT, fence);
        await this.inPathOrder(session, path, url, () =>
          retryHomeserverWrite(
            HttpMethod.PUT,
            () => this.withActiveSession(session, () => session.storage.putBytes(path, retryBody.slice())),
            { beforeAttempt, ...retryOptions },
          ),
        );
        return;
      } catch (error) {
        return handleError({ error, additionalContext: { url, method: HttpMethod.PUT } });
      }
    }

    if (!isHttpUrl(url) || extractPubkyZ32(url) !== null) {
      throw Err.validation(
        ValidationErrorCode.INVALID_INPUT,
        'Blob uploads must target an owned /pub/* or /priv/* path for the current session.',
        {
          service: ErrorService.Homeserver,
          operation: 'putBlob',
          context: { url, statusCode: HttpStatusCode.BAD_REQUEST },
        },
      );
    }

    const response = await retryHomeserverWrite(
      HttpMethod.PUT,
      () => this.fetch({ url, options: { method: HttpMethod.PUT, body: retryBody.slice() } }),
      retryOptions,
    );
    await assertOk({ response, url, operation: 'putBlob' });
  }

  /**
   * Lists files in a directory from the homeserver.
   *
   * Supports pagination with cursor and optional filtering.
   *
   * @param {string} baseDirectory - Base directory path to list files from.
   * @param {string} [cursor] - Optional cursor for pagination.
   * @param {boolean} [reverse=false] - Whether to list in reverse order.
   * @param {number} [limit=500] - Maximum number of files to return.
   * @returns {Promise<string[]>} Array of file URLs.
   */
  static async list({
    baseDirectory,
    cursor,
    reverse = false,
    limit = LIST_DEFAULT_LIMIT,
  }: THomeserverListParams): Promise<string[]> {
    const pubkySdk = this.getPubkySdk();
    try {
      const owned = this.resolveOwnedSessionPath(baseDirectory);
      if (owned) {
        const dirPath = owned.path.endsWith('/') ? owned.path : (`${owned.path}/` as StoragePath<string>);
        const files = await this.withActiveSession(owned.session, () =>
          owned.session.storage.list(dirPath, cursor ?? null, reverse, limit, false),
        );
        Logger.debug('List successful', { baseDirectory, filesCount: files.length });
        return files;
      }

      const files = await pubkySdk.publicStorage.list(baseDirectory as Address, cursor ?? null, reverse, limit, false);
      Logger.debug('List successful', { baseDirectory, filesCount: files.length });
      return files;
    } catch (error) {
      // 404 here is not an error: missing directory means empty list. Bypass handleError to avoid Sentry capture.
      if (extractStatusCode(error) === HttpStatusCode.NOT_FOUND) {
        Logger.warn('[homeserver:list]', { outcome: 'fallback', reason: 'not_found', baseDirectory });
        return [];
      }
      return handleError({ error, additionalContext: { url: baseDirectory, baseDirectory } });
    }
  }

  /**
   * Lists ALL files under a base directory by paginating with a cursor until exhausted.
   *
   * A single `list` call is page-limited and must use a finite limit: non-finite values
   * (e.g. Infinity) coerce to 0 at the SDK's WASM boundary and silently return an empty
   * page. Paginating with the SDK cursor ("start after this URL") is the only reliable
   * way to enumerate an entire directory.
   *
   * @param {string} baseDirectory - Base directory path to list all files from.
   * @returns {Promise<string[]>} Array of every file URL under the directory.
   */
  static async listAll({ baseDirectory }: THomeserverListAllParams): Promise<string[]> {
    const files: string[] = [];
    let cursor: string | undefined;

    for (;;) {
      const batch = await this.list({ baseDirectory, cursor, limit: LIST_DEFAULT_LIMIT });
      files.push(...batch);

      if (batch.length < LIST_DEFAULT_LIMIT) {
        return files;
      }

      cursor = batch[batch.length - 1];
    }
  }

  /**
   * Copies `session`'s 0.x tree to social v1 with `pubky-social-migrator`, which resolves with a
   * report whatever the outcome (its own transport retries are the documented second retry
   * policy, `docs/error-handling.md`). The session is passed explicitly: the gate runs before
   * the session enters the auth store (ADR-0026), so nothing here reads the store. One run per
   * account across tabs: a tab that finds the Web Lock taken waits for it (`onLockWait` fires
   * once when another tab holds it) and then reads the flag the first tab wrote. Without
   * `navigator.locks` the run is unlocked. The migrator loads a wasm, so it is imported only
   * here and only when a run starts, which keeps its chunk out of the main bundle and out of
   * the service-worker precache (`docs/pwa.md`).
   */
  static async migrateToSocialV1({
    session,
    signal,
    onProgress,
    onLockWait,
  }: {
    session: Session;
    signal?: AbortSignal;
    onProgress?: (event: ProgressEvent) => void;
    onLockWait?: () => void;
  }): Promise<MigrationReport> {
    const [{ runMigration }, { sdkPort }] = await Promise.all([
      import('pubky-social-migrator'),
      import('pubky-social-migrator/pubky-sdk'),
    ]);
    const locks = globalThis.navigator?.locks;
    const lock: MigrationLock | undefined = locks
      ? async (name, fn) => {
          if (onLockWait) {
            const { held = [] } = await locks.query().catch(() => ({ held: [] }));
            if (held.some((entry) => entry.name === name)) onLockWait();
          }
          try {
            return await locks.request(name, { signal }, fn);
          } catch (error) {
            // The wait itself was aborted: run unlocked (`null` would mean "another tab holds
            // it"), the engine stops at once with `ABORTED` and nothing is written.
            if (signal?.aborted) return await fn({});
            throw error;
          }
        }
      : undefined;
    return await runMigration({
      owner: session.info.publicKey.z32(),
      port: sdkPort(session),
      caps: session.info.capabilities,
      lock,
      signal,
      onProgress,
    });
  }

  /**
   * Deletes a file from the homeserver.
   *
   * @param {string} url - Pubky URL of the file to delete.
   * @param {boolean} [singleAttempt] - One attempt, for a caller that runs its own retry loop.
   */
  static async delete(url: string, { singleAttempt }: { singleAttempt?: boolean } = {}) {
    await this.request({ method: HttpMethod.DELETE, url, singleAttempt });
    Logger.debug('Delete successful', { url });
  }

  /**
   * Deletes a homeserver resource for cleanup flows: 404 counts as success
   * ("already gone" is the desired end state) and transient failures retry
   * with a short backoff. Mirrors the hardened delete account deletion uses
   * (Sentry PUBKY-APP-6Y) so a single transport blip doesn't orphan resources.
   * This loop is the retry: each attempt is a single DELETE, so the write
   * retry policy's attempts don't multiply with these.
   */
  static async deleteIdempotent(url: string) {
    for (let attempt = 1; ; attempt++) {
      try {
        await this.delete(url, { singleAttempt: true });
        return;
      } catch (error) {
        if (hasHttpStatus(error, HttpStatusCode.NOT_FOUND)) {
          return;
        }
        if (attempt >= DELETE_IDEMPOTENT_MAX_ATTEMPTS) {
          throw error;
        }
        Logger.warn('[HomeserverService] Delete failed, retrying', { url, attempt });
        await sleep(DELETE_IDEMPOTENT_RETRY_DELAY_MS * attempt);
      }
    }
  }

  /**
   * Fetches a resource from the homeserver.
   *
   * @param {string} url - Pubky URL to fetch.
   * @returns {Promise<Response>} The fetch response.
   */
  static async get(url: string): Promise<Response> {
    const pubkySdk = this.getPubkySdk();
    try {
      const owned = this.resolveOwnedSessionPath(url);
      if (owned) {
        return await this.withActiveSession(owned.session, () =>
          getOwnedResponse({ session: owned.session, path: owned.path, url }),
        );
      }

      if (isHttpUrl(url)) {
        return await pubkySdk.client.fetch(url, { credentials: 'omit' });
      }

      return await pubkySdk.publicStorage.get(url as Address);
    } catch (error) {
      return handleError({ error, additionalContext: { url, method: HttpMethod.GET } });
    }
  }

  /** Read binary content, rejecting missing resources and failed HTTP responses. */
  static async getBytes(url: string): Promise<Uint8Array> {
    try {
      const response = await this.get(url);
      await assertOk({ response, url, operation: 'getBytes' });
      return new Uint8Array(await response.arrayBuffer());
    } catch (error) {
      return handleError({ error, additionalContext: { url, method: HttpMethod.GET, operation: 'getBytes' } });
    }
  }

  /**
   * Read an owned resource with its server modification time. Only a 404 means absence;
   * missing authentication, insufficient permissions and transport failures reject.
   *
   * `session` reads through a session that is not (yet) in the auth store: the social
   * migration gate checks the done flag before the session enters the store (ADR-0026).
   * Without it the active session resolves the owned path as every other read does.
   */
  static async getBytesIfExists(url: string, session?: Session): Promise<THomeserverBytesResult | null> {
    const owned = session
      ? resolveOwnedSessionPath({ url, session, allowedPrefixes: STORAGE_PATH_PREFIXES })
      : this.resolveOwnedSessionPath(url);
    if (!owned) {
      throw Err.auth(AuthErrorCode.UNAUTHORIZED, 'Reading owned storage requires a session for the resource owner.', {
        service: ErrorService.Homeserver,
        operation: 'getBytesIfExists',
        context: { url },
      });
    }

    try {
      const response = await this.withActiveSession(owned.session, async () => {
        const response = await owned.session.storage.get(owned.path);
        if (response.status !== HttpStatusCode.NOT_FOUND)
          await assertOk({ response, url, operation: 'getBytesIfExists' });
        return response;
      });
      if (response.status === HttpStatusCode.NOT_FOUND) return null;
      const lastModified = Date.parse(response.headers.get('last-modified') ?? '');
      return {
        bytes: new Uint8Array(await response.arrayBuffer()),
        modifiedAt: Number.isNaN(lastModified) ? null : lastModified,
      };
    } catch (error) {
      if (extractStatusCode(error) === HttpStatusCode.NOT_FOUND) return null;
      return handleError({ error, additionalContext: { url, method: HttpMethod.GET } });
    }
  }

  /**
   * Reads a JSON resource past the browser HTTP cache. The homeserver sends `Last-Modified`
   * without `max-age`, so a plain GET can be answered from a heuristically fresh cached copy.
   * Throws an `AppError` on a non-OK response or a failed read (a body that breaks off
   * mid-read included); resolves `undefined` for an empty or invalid body.
   *
   * @param url - Pubky URL to read.
   */
  static async getFreshJson<T>(url: string): Promise<T | undefined> {
    try {
      const response = await this.fetch({ url, options: { method: HttpMethod.GET, cache: 'no-store' } });
      await assertOk({ response, url, operation: 'getFreshJson' });
      return await parseResponseOrUndefined<T>({ response, operation: 'getFreshJson', url });
    } catch (error) {
      throw toAppError(error, ErrorService.Homeserver, 'getFreshJson');
    }
  }

  /**
   * Bytes of a public resource read past the browser HTTP cache, for a read-modify-write (the
   * head version of a post, a profile edit) where a heuristically fresh cached copy would undo
   * another device's write (see `getFreshJson`). Rejects a missing resource and a failed HTTP
   * response like `getBytes`. Public only: the raw fetch carries no session, so an owned `/priv/`
   * path answers 401; read those through `request` (GET) or `getBytesIfExists`.
   *
   * @param url - Pubky URL to read.
   */
  static async getFreshBytes(url: string): Promise<Uint8Array> {
    try {
      const response = await this.fetch({ url, options: { method: HttpMethod.GET, cache: 'no-store' } });
      await assertOk({ response, url, operation: 'getFreshBytes' });
      return new Uint8Array(await response.arrayBuffer());
    } catch (error) {
      throw toAppError(error, ErrorService.Homeserver, 'getFreshBytes');
    }
  }

  /**
   * Checks whether a homeserver resource exists without treating an expected 404 as an error.
   * Storage SDK probes return a boolean; plain HTTP URLs retain explicit response handling.
   */
  static async exists(url: string): Promise<boolean> {
    const pubkySdk = this.getPubkySdk();

    try {
      const owned = this.resolveOwnedSessionPath(url);
      if (owned) return await this.withActiveSession(owned.session, () => owned.session.storage.exists(owned.path));
      if (isHttpUrl(url)) {
        const response = await pubkySdk.client.fetch(url, { credentials: 'omit' });
        if (response.status === HttpStatusCode.NOT_FOUND) return false;
        await assertOk({ response, url, operation: 'exists' });
        return true;
      }

      return await pubkySdk.publicStorage.exists(url as Address);
    } catch (error) {
      return handleError({
        error,
        additionalContext: { url, method: HttpMethod.GET, operation: 'exists' },
      });
    }
  }

  /**
   * Generates a signup token for dev/test environments.
   * Calls the server-side API route to keep admin credentials secure.
   *
   * @security Admin credentials are never exposed to the client.
   * The actual token generation happens server-side via /api/dev/signup-token.
   */
  static async generateSignupToken() {
    // Allow in development or when Cypress is running (for E2E tests in production builds)
    const isCypressRunning = typeof window !== 'undefined' && 'Cypress' in window;
    const isProduction = process.env.NODE_ENV === 'production';

    if (isProduction && !isCypressRunning) {
      throw Err.validation(
        ValidationErrorCode.INVALID_INPUT,
        'generateSignupToken is only available in non-production environments.',
        {
          service: ErrorService.Homeserver,
          operation: 'generateSignupToken',
          context: { isProduction, isCypressRunning },
        },
      );
    }

    // Call server-side API route to generate token (keeps admin credentials secure)
    const response = await fetch('/api/dev/signup-token', {
      method: 'GET',
    });

    if (!response.ok) {
      throw httpResponseToError(response, ErrorService.Homeserver, 'generateSignupToken', '/api/dev/signup-token');
    }

    const data = await response.json();
    if (!data.token) {
      throw Err.server(ServerErrorCode.UNKNOWN_ERROR, 'No token received from server', {
        service: ErrorService.Homeserver,
        operation: 'generateSignupToken',
      });
    }

    return data.token;
  }

  /**
   * Subscribe to homeserver `/events-stream` for a user's directory subtree (SDK SSE wrapper).
   * Used for mute-list sync; callers own {@link ReadableStreamDefaultReader} lifecycle.
   *
   * A `/priv/` prefix is served only to a session of the tree's owner (SDK 0.16.2 `path()`), so
   * the signed-in session is attached when it is that owner's, and the call fails as an auth
   * error otherwise instead of letting the homeserver answer 401.
   */
  static async subscribeUserEventStreamForPath(params: {
    userZ32: TPubkyModel;
    cursor: string | null;
    pathPrefix: string;
  }): Promise<ReadableStream<THomeserverUserEvent>> {
    try {
      const pubkySdk = this.getPubkySdk();
      const pk = PublicKey.from(params.userZ32);
      let builder = pubkySdk.eventStreamForUser(pk, params.cursor);
      if (params.pathPrefix.startsWith(PRIVATE_STORAGE_PREFIX)) {
        const session = useAuthStore.getState().selectSession();
        if (!session) {
          throw Err.auth(AuthErrorCode.SESSION_EXPIRED, 'A private event stream needs a signed-in session.', {
            service: ErrorService.Homeserver,
            operation: HOMESERVER_EVENT_STREAM_SUBSCRIBE_OPERATION,
            context: { pathPrefix: params.pathPrefix },
          });
        }
        if (session.info.publicKey.z32() !== params.userZ32) {
          throw Err.auth(AuthErrorCode.UNAUTHORIZED, 'A private event stream is served only to its owner.', {
            service: ErrorService.Homeserver,
            operation: HOMESERVER_EVENT_STREAM_SUBSCRIBE_OPERATION,
            context: { pathPrefix: params.pathPrefix },
          });
        }
        builder = builder.session(session);
      }
      const stream = (await builder
        .path(params.pathPrefix)
        .live()
        .subscribe()) as ReadableStream<HomeserverSdkUserEvent>;

      return this.normalizeUserEventStream(stream);
    } catch (error) {
      // `operation` is matched by the `homeserver-event-stream-connect` Sentry drop rule:
      // the mute-list coordinator reconnects on connect failures by design.
      return handleError({
        error,
        additionalContext: { pathPrefix: params.pathPrefix, operation: HOMESERVER_EVENT_STREAM_SUBSCRIBE_OPERATION },
      });
    }
  }

  private static normalizeUserEventStream(
    stream: ReadableStream<HomeserverSdkUserEvent>,
  ): ReadableStream<THomeserverUserEvent> {
    const reader = stream.getReader();

    return new ReadableStream<THomeserverUserEvent>({
      async pull(controller) {
        const { done, value } = await reader.read();

        if (done) {
          controller.close();
          return;
        }

        try {
          controller.enqueue({
            cursor: value.cursor,
            eventType: value.eventType,
          });
        } finally {
          try {
            value.free();
          } catch {
            // Ignore WASM dispose errors.
          }
        }
      },
      async cancel(reason) {
        await reader.cancel(reason).catch(() => {});
      },
    });
  }
}
